import { Card } from '@yleisradio/areena-types';
import React, { useEffect, useRef, useState } from 'react';
import logger from 'services/logger';
import { trackCardView } from 'services/snowplowAnalytics/snowplowAnalytics';
import { getEntityIdForCard } from 'utils/card';
import { GroupEngagementData } from './types';
import { ViewModeType } from 'services/snowplowAnalytics/contextGenerators';

type Props = {
  card: Card | undefined;
  cardIndex: number;
  children?: React.ReactNode;
  groupData?: GroupEngagementData;
  viewUuid: string;
  viewMode: ViewModeType;
  wrapperLabel?: string | undefined;
  wrapperType?: 'main' | 'overlay' | undefined;
  metaItemId?: string | undefined;
  entityId?: string | undefined;
  searchQuery?: string | undefined;
  searchIdentifier?: string | undefined;
};

export const ViewableCard: React.FC<Props> = ({
  metaItemId,
  card,
  cardIndex,
  children,
  groupData,
  viewUuid,
  viewMode,
  wrapperLabel,
  wrapperType,
  entityId: entityIdProp,
  searchQuery,
  searchIdentifier,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isSent, setSent] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || isSent) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (!card || !entry || !entry.isIntersecting) return;

      const entityId = getEntityIdForCard(card) || entityIdProp;

      if (!entityId) {
        logger.error({ card }, 'No entityId found for card');
        return;
      }
      const pageloadAnalytics = card.controls?.[cardIndex]?.analytics?.pageload;
      const packageId =
        pageloadAnalytics?.yle?.entity_type === 'areena_package'
          ? pageloadAnalytics?.yle?.id
          : undefined;

      trackCardView({
        cardIndex,
        cardTitle: card.title,
        cardViewUuid: viewUuid,
        entityId,
        imageId: card.image?.id,
        groupData,
        packageId,
        viewMode,
        wrapperLabel,
        wrapperType,
        searchQuery,
        searchIdentifier,
      });
      setSent(true);
    });

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [
    card,
    cardIndex,
    entityIdProp,
    groupData,
    isSent,
    viewUuid,
    viewMode,
    wrapperLabel,
    wrapperType,
    metaItemId,
    searchQuery,
    searchIdentifier,
  ]);

  return <div ref={ref}>{children}</div>;
};
